import { useEffect, useState } from 'react';
import { Button } from '../../../components/Form/Button';
import { useOrganizationStore } from '../../../shared/organization/organizationStore';
import { useOrgUserStore } from '../../../shared/organization/orgUserStore';
import { navigate } from '../../../utils/navigation';
import { useAuthStore } from '../../auth/authStore';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  getOrgMembersAction,
  updateOrgMembersActions,
} from '../../../shared/organization/organizationActions';
import { useModalStore } from '../../../shared/store/uiStore';
import UserSelect from '../components/UserSelect';
import { UserRole } from '../../auth/types';

const LimitExceeded = () => {
  const org = useOrganizationStore((state) => state.organization);
  const auth = useAuthStore((state) => state.auth);
  const orgUsers = useOrgUserStore((state) => state.items);
  const [difference, setDifference] = useState<number>(0);
  const [changedUsers, setChangedUsers] = useState<Record<string, string>>({});
  const openModal = useModalStore((state) => state.openModal);

  const people = Object.values(orgUsers).reduce<
    { name: string; role: string; uid: string }[]
  >((acc, user) => {
    if (
      user.role !== 'viewer' &&
      auth?.uid !== user.uid &&
      user.role !== 'owner'
    ) {
      acc.push({
        uid: user.uid,
        name: user.displayName,
        role: user.role,
        //   imageUrl: user.imageUrl || '/default-avatar.png',
      });
    }
    return acc;
  }, []);

  useEffect(() => {
    if (org && !org.overLimit) {
      navigate('/v1/dashboard');
    }
    if (org && org.userLimit && org.editorCount) {
      setDifference(org.userLimit - org.editorCount);
      getOrgMembersAction();
    }
  }, [org]);

  const handleRoleChange = (uid: string, newRole: string) => {
    setChangedUsers((prev) => ({
      ...prev,
      [uid]: newRole as UserRole,
    }));

    if (newRole === 'viewer') {
      setDifference((prev) => prev + 1);
    } else if (newRole === 'editor') {
      setDifference((prev) => prev - 1);
    }
  };

  const handleSaveChanges = async () => {
    const updates = Object.entries(changedUsers).map(([uid, role]) => ({
      uid,
      data: { role: role as UserRole | undefined },
      type: 'update' as const,
    }));

    if (updates.length > 0 && difference >= 0) {
      await updateOrgMembersActions(updates);
      setChangedUsers({});
      navigate('/v1/dashboard');
    }
  };

  const handleUpgradePlan = () => {
    openModal({
      id: 'upgrade-plan-modal',
      title: 'Upgrade Plan',
      size: 'sm',
      content: <UserSelect />,
    });
  };

  if (!org?.userLimit || !org?.editorCount) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
        <div className="sm:mx-auto sm:w-full sm:max-w-md mb-10">
          <h2 className="mt-6 text-center text-2xl/9 font-bold tracking-tight text-gray-900">
            Loading...
          </h2>
        </div>
        <div className="sm:mx-auto sm:w-full sm:max-w-[480px]">
          <div className="bg-white px-6 py-12 shadow-sm sm:rounded-lg sm:px-12">
            <p className="text-center text-sm/6 text-gray-500 mb-4">
              Please wait while we fetch your organization details.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
      <div className="sm:mx-auto sm:w-full sm:max-w-md mb-10">
        <h2 className="mt-6 text-center text-2xl/9 font-bold tracking-tight text-gray-900">
          User Limit Exceeded
        </h2>
      </div>
      <div className="sm:mx-auto sm:w-full sm:max-w-[480px]">
        <div className="bg-white px-6 py-12 shadow-sm sm:rounded-lg sm:px-12">
          <p className="text-center text-sm/6 text-gray-500 mb-4">
            Your organization has exceeded the maximum number of users allowed.
            Please upgrade your plan to add more user licenses or downgrade some
            users to viewers.
          </p>
          <div className="flex justify-center gap-x-4 mb-6">
            <p className="text-sm/6 text-gray-900">
              Licenses Available:{' '}
              <span
                className={`${
                  difference >= 0 ? 'text-green-500' : 'text-red-500'
                } font-semibold`}
              >
                {difference}
              </span>
            </p>
          </div>
          <div className="flex justify-center">
            <Button
              type="button"
              onClick={handleUpgradePlan}
              className="inline-flex items-center px-4 py-2 bg-primary text-white font-semibold rounded-md hover:bg-secondary transition-colors"
            >
              Upgrade Plan
            </Button>
          </div>
          <div className="relative mt-6 mb-0">
            <div
              aria-hidden="true"
              className="absolute inset-0 flex items-center"
            >
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-2 text-sm text-gray-500">Users</span>
            </div>
          </div>
          <div className="w-full">
            <ul role="list" className="divide-y divide-gray-100 w-full">
              {people.map((person) => (
                <li key={person.uid} className="flex gap-x-4 py-5 w-full">
                  <div className="min-w-0 flex justify-between items-center w-full">
                    <p className="text-sm/6 font-semibold text-gray-900">
                      {person.name}
                    </p>
                    <div>
                      <label
                        htmlFor="location"
                        className="sr-only block text-sm/6 font-medium text-gray-900"
                      >
                        Location
                      </label>
                      <div className="mt-2 grid grid-cols-1">
                        <select
                          id="location"
                          name="location"
                          defaultValue={person.role}
                          onChange={(e) =>
                            handleRoleChange(person.uid, e.target.value)
                          }
                          className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
                        >
                          <option value="editor">Editor</option>
                          <option value="viewer">Viewer</option>
                        </select>
                        <ChevronDownIcon
                          aria-hidden="true"
                          className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4"
                        />
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <Button
              type="button"
              onClick={handleSaveChanges}
              className="mt-2 w-full text-full py-2"
              disabled={difference < 0}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
export default LimitExceeded;
